import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import {
  alertRuleResponseSchema,
  alertTestFireResponseSchema,
  createAlertRuleRequestSchema,
  updateAlertRuleRequestSchema,
  type AlertRuleResponse,
  type AlertTestFireResponse,
  type CreateAlertRuleRequest,
  type UpdateAlertRuleRequest,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { alertRuleQueryKeys } from "./keys";

/**
 * POST /api/communities/:communityId/alert-rules — create a new rule.
 * Invalidates the list cache on success so the freshly-created rule
 * appears without a manual refetch.
 */
export function useCreateAlertRuleMutation(
  communityId: string | null,
): UseMutationResult<AlertRuleResponse, Error, CreateAlertRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation<AlertRuleResponse, Error, CreateAlertRuleRequest>({
    mutationFn: async (input) => {
      if (communityId === null) {
        throw new Error("communityId is required");
      }
      const body = createAlertRuleRequestSchema.parse(input);
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/alert-rules/`,
        { method: "POST", body },
      );
      return alertRuleResponseSchema.parse(raw);
    },
    onSuccess: () => {
      if (communityId === null) return;
      void queryClient.invalidateQueries({
        queryKey: alertRuleQueryKeys.list(communityId),
      });
    },
  });
}

export interface UpdateAlertRuleArgs {
  ruleId: string;
  patch: UpdateAlertRuleRequest;
}

/**
 * PATCH /api/communities/:communityId/alert-rules/:ruleId — partial update.
 * On success, optimistically updates the list cache (replacing the row
 * with the server's authoritative response) so the toggle / threshold
 * change is reflected without a refetch.
 */
export function useUpdateAlertRuleMutation(
  communityId: string | null,
): UseMutationResult<AlertRuleResponse, Error, UpdateAlertRuleArgs> {
  const queryClient = useQueryClient();
  return useMutation<AlertRuleResponse, Error, UpdateAlertRuleArgs>({
    mutationFn: async ({ ruleId, patch }) => {
      if (communityId === null) {
        throw new Error("communityId is required");
      }
      const body = updateAlertRuleRequestSchema.parse(patch);
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/alert-rules/${encodeURIComponent(ruleId)}`,
        { method: "PATCH", body },
      );
      return alertRuleResponseSchema.parse(raw);
    },
    onSuccess: (next) => {
      if (communityId === null) return;
      const key = alertRuleQueryKeys.list(communityId);
      const current = queryClient.getQueryData<AlertRuleResponse[]>(key);
      if (current) {
        queryClient.setQueryData<AlertRuleResponse[]>(
          key,
          current.map((r) => (r.id === next.id ? next : r)),
        );
      } else {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

/**
 * DELETE /api/communities/:communityId/alert-rules/:ruleId. Returns no
 * body on success (the route emits 204). Invalidates the list cache.
 */
export function useDeleteAlertRuleMutation(
  communityId: string | null,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (ruleId) => {
      if (communityId === null) {
        throw new Error("communityId is required");
      }
      await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/alert-rules/${encodeURIComponent(ruleId)}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      if (communityId === null) return;
      void queryClient.invalidateQueries({
        queryKey: alertRuleQueryKeys.list(communityId),
      });
    },
  });
}

/**
 * POST /api/communities/:communityId/alert-rules/:ruleId/test-fire.
 *
 * Test-fire is a NON-mutating preview — it does not write to the DB and
 * does not invalidate any cache. We use a mutation hook anyway because:
 *   1. The action is user-initiated (button click), not auto-fetched.
 *   2. TanStack Query mutations expose pending/error states a query
 *      `enabled: false` would not.
 */
export function useTestFireAlertRuleMutation(
  communityId: string | null,
): UseMutationResult<AlertTestFireResponse, Error, string> {
  return useMutation<AlertTestFireResponse, Error, string>({
    mutationFn: async (ruleId) => {
      if (communityId === null) {
        throw new Error("communityId is required");
      }
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/alert-rules/${encodeURIComponent(ruleId)}/test-fire`,
        { method: "POST" },
      );
      return alertTestFireResponseSchema.parse(raw);
    },
  });
}
